import { type RefObject, useCallback, useRef, useState } from 'react';

import { type Bounds, getBounds, isDefined, isNotDefined } from '@enterprise/core';

import type { IntersectionCallback } from '#utilities/types/intersection-callback.type';

import { useResizeObserver } from '#browser/resize-observer/resize-observer.hook';
import { useDidUnmount } from '#life-cycle/did-unmount.hook';
import { useDeepCompareEffect } from '#misc/compare/deep-compare-effect.hook';
import { useDeepCompareLayoutEffect } from '#misc/compare/deep-compare-layout-effect.hook';
import { getIntersectionObserver } from '#utilities/get-intersection-observer';

/**
 * React hook to observe and retrieve the size (bounds) of a DOM element.
 *
 * This hook returns a ref and the current bounds of the referenced element.
 * It uses a ResizeObserver to update the bounds whenever the element's size changes.
 * If an external ref is provided, it will be used; otherwise, an internal ref is created.
 *
 * @template T - The type of the element to observe, defaults to HTMLDivElement.
 * @param externalRef - An optional React ref object pointing to the target element.
 * @returns A tuple containing the ref to be attached to the element and its current bounds
 * (or undefined if not available).
 *
 * @example
 * ```tsx
 * const [ref, size] = useElementSize<HTMLDivElement>(null);
 * return <div ref={ref}>Width: {size?.width}, Height: {size?.height}</div>;
 * ```
 */
export const useElementSize = <T extends Element = HTMLDivElement>(externalRef: RefObject<T | null> | null) => {
  const [size, setSize] = useState<Bounds | undefined>(undefined);
  const [isVisible, setIsVisible] = useState(false);

  const resizeRafRef = useRef<null | number>(null);
  const internalRef = useRef<T | null>(null);
  const targetRef = externalRef ?? internalRef;

  const instance = getIntersectionObserver();

  useDeepCompareEffect(() => {
    const targetElement = targetRef.current;

    if (isNotDefined(instance) || isNotDefined(targetElement)) {
      return;
    }

    let subscribed = true;

    const handler: IntersectionCallback = (data) => {
      if (subscribed) {
        setIsVisible(data.isVisible);
      }
    };

    instance.add(targetElement, handler);

    return () => {
      subscribed = false;
      instance.remove(targetElement, handler);
    };
  }, [instance, targetRef.current]);

  useDeepCompareLayoutEffect(() => {
    const targetElement = targetRef.current;

    if (isNotDefined(targetElement) || !isVisible) {
      setSize(undefined);

      return;
    }

    setSize(getBounds(targetElement));
  }, [isVisible, targetRef.current]);

  const onResize = useCallback((bounds: Bounds) => {
    if (isDefined(resizeRafRef.current)) {
      cancelAnimationFrame(resizeRafRef.current);
    }

    resizeRafRef.current = requestAnimationFrame(() => {
      setSize(bounds);
    });
  }, []);

  useResizeObserver({ onResize, target: targetRef });

  useDidUnmount(() => {
    if (isDefined(resizeRafRef.current)) {
      cancelAnimationFrame(resizeRafRef.current);
    }
  });

  return [targetRef, size] as const;
};
